import gradeK from "../../../public/vocabularies/grade-K.json";
import grade1 from "../../../public/vocabularies/grade-1.json";
import grade2 from "../../../public/vocabularies/grade-2.json";
import grade3 from "../../../public/vocabularies/grade-3.json";
import grade4 from "../../../public/vocabularies/grade-4.json";
import grade5 from "../../../public/vocabularies/grade-5.json";
import grade6 from "../../../public/vocabularies/grade-6.json";
import type {
  ProfessionalContentInventoryUnit,
  ProfessionalContentType,
  ProfessionalDifficulty,
} from "./types";

export interface LaunchContentItemInput {
  id: string;
  title: string;
  text: string;
  focusSkills: string[];
  difficulty: ProfessionalDifficulty;
  benchmarkApproved?: boolean;
  language?: string;
  source?: string;
  syntaxValidated?: boolean;
}

export interface AppendixEContentTarget {
  packId: string;
  target: number;
  unit: ProfessionalContentInventoryUnit;
  requiredPatterns: readonly string[];
  benchmarkEligibility: string;
  authority: "Appendix E" | "9.2";
  coveredCategories: readonly ClassAContentCategory[];
}

export const CLASS_A_CONTENT_CATEGORIES = [
  "general-english-fluency",
  "common-words",
  "natural-sentences",
  "capitalization-punctuation",
  "numbers-dates",
  "symbols",
  "writing-dialogue",
  "markdown",
  "javascript-typescript",
  "python",
  "html-css",
  "json-yaml",
  "sql",
  "chat-commands",
  "research-citations",
  "adaptive-problem-keys",
] as const;

export type ClassAContentCategory = (typeof CLASS_A_CONTENT_CATEGORIES)[number];

export const APPENDIX_E_CONTENT_TARGETS: readonly AppendixEContentTarget[] = [
  { packId: "common-words", target: 1_000, unit: "words", requiredPatterns: ["frequency-balanced", "common-words"], benchmarkEligibility: "Word-count candidate", authority: "Appendix E", coveredCategories: ["common-words"] },
  { packId: "prose-flow", target: 250, unit: "paragraphs", requiredPatterns: ["mixed-case", "punctuation"], benchmarkEligibility: "Selected locked passages", authority: "Appendix E", coveredCategories: ["general-english-fluency", "natural-sentences"] },
  { packId: "shift-punctuation", target: 150, unit: "items", requiredPatterns: ["capitalization", "punctuation", "shift-transitions"], benchmarkEligibility: "Selected", authority: "9.2", coveredCategories: ["capitalization-punctuation"] },
  { packId: "writing-dialogue", target: 150, unit: "paragraphs", requiredPatterns: ["quotes", "apostrophes", "em-dashes", "dialogue"], benchmarkEligibility: "Selected", authority: "Appendix E", coveredCategories: ["writing-dialogue"] },
  { packId: "numbers-data", target: 200, unit: "items", requiredPatterns: ["integers", "decimals", "dates", "times", "percent", "currency"], benchmarkEligibility: "Selected", authority: "Appendix E", coveredCategories: ["numbers-dates"] },
  { packId: "symbols-operators", target: 200, unit: "items", requiredPatterns: ["shifted-symbols", "punctuation-transitions"], benchmarkEligibility: "Selected", authority: "Appendix E", coveredCategories: ["symbols"] },
  { packId: "markdown-docs", target: 100, unit: "snippets", requiredPatterns: ["headings", "links", "lists", "emphasis", "code-fences"], benchmarkEligibility: "No cross-pack record", authority: "Appendix E", coveredCategories: ["markdown"] },
  { packId: "coding-symbols", target: 150, unit: "snippets", requiredPatterns: ["braces", "arrows", "identifiers", "generics"], benchmarkEligibility: "Language-specific candidate", authority: "Appendix E", coveredCategories: ["javascript-typescript"] },
  { packId: "python-code", target: 150, unit: "snippets", requiredPatterns: ["indentation", "colons", "comprehensions", "calls"], benchmarkEligibility: "Language-specific candidate", authority: "Appendix E", coveredCategories: ["python"] },
  { packId: "web-code", target: 150, unit: "snippets", requiredPatterns: ["tags", "attributes", "selectors", "declarations"], benchmarkEligibility: "Language-specific candidate", authority: "Appendix E", coveredCategories: ["html-css"] },
  { packId: "structured-json", target: 100, unit: "snippets", requiredPatterns: ["quotes", "delimiters", "indentation"], benchmarkEligibility: "Format-specific candidate", authority: "Appendix E", coveredCategories: ["json-yaml"] },
  { packId: "structured-yaml", target: 100, unit: "snippets", requiredPatterns: ["delimiters", "indentation", "mapping"], benchmarkEligibility: "Format-specific candidate", authority: "Appendix E", coveredCategories: ["json-yaml"] },
  { packId: "sql-queries", target: 100, unit: "snippets", requiredPatterns: ["keywords", "punctuation", "identifiers"], benchmarkEligibility: "Language-specific candidate", authority: "Appendix E", coveredCategories: ["sql"] },
  { packId: "chat-commands", target: 300, unit: "items", requiredPatterns: ["short-bursts", "slash-commands", "usernames"], benchmarkEligibility: "Response-mode only", authority: "Appendix E", coveredCategories: ["chat-commands"] },
  { packId: "research-citations", target: 100, unit: "items", requiredPatterns: ["parentheses", "years", "names", "reference-patterns"], benchmarkEligibility: "Selected", authority: "Appendix E", coveredCategories: ["research-citations"] },
  { packId: "adaptive-problem-keys", target: 500, unit: "templates", requiredPatterns: ["key-insertion", "bigram-insertion", "trigram-insertion"], benchmarkEligibility: "No", authority: "Appendix E", coveredCategories: ["adaptive-problem-keys"] },
] as const;

export function getAppendixEContentTarget(
  packId: string,
): AppendixEContentTarget | null {
  return APPENDIX_E_CONTENT_TARGETS.find((entry) => entry.packId === packId) ?? null;
}

function titleIndex(index: number): string {
  return String(index + 1).padStart(3, "0");
}

function choose<T>(values: readonly T[], index: number, stride = 1): T {
  return values[(index * stride) % values.length]!;
}

function generatedItem(
  packId: string,
  index: number,
  text: string,
  focusSkills: string[],
  difficulty: ProfessionalDifficulty,
  language?: string,
): LaunchContentItemInput {
  return {
    id: `${packId}-catalog-${titleIndex(index)}`,
    title: `Catalog ${titleIndex(index)}`,
    text,
    focusSkills,
    difficulty,
    language,
    syntaxValidated: Boolean(language),
  };
}

const PROSE_SUBJECTS = [
  "The museum coordinator", "A neighborhood librarian", "The service designer",
  "An evening instructor", "The workshop manager", "A patient researcher",
  "The community gardener", "A transit planner", "The studio editor",
  "A careful technician", "The volunteer organizer", "A local reporter",
  "The archive assistant", "A product specialist", "The trail steward",
  "A new team lead", "The rehearsal director", "A clinic scheduler",
  "The building caretaker", "A curious student",
] as const;

const PROSE_TASKS = [
  "reviewed the open questions before choosing the next practical step",
  "organized the shared notes so another person could continue without delay",
  "tested a small change and recorded what actually happened",
  "prepared a clear handoff with the decision, risk, owner, and due date",
  "compared two possible routes before committing time and materials",
  "revised the first draft until the main idea was easy to follow",
  "checked the available evidence instead of relying on a confident guess",
  "set aside a quiet hour for the task that required the most attention",
  "asked one precise question that helped the group resolve its disagreement",
  "noticed a recurring problem and documented a simple recovery path",
  "finished the difficult section at a steady pace without rushing the review",
] as const;

const PROSE_OBSERVATIONS = [
  "The work became easier to verify because each assumption remained visible.",
  "A short written record prevented the same uncertainty from returning later.",
  "The slower beginning saved time once the important details were understood.",
  "Everyone could see what had changed and what still required a decision.",
  "The result was modest, useful, and strong enough to guide another attempt.",
  "Calm attention made the complicated sequence feel manageable rather than urgent.",
  "The final review found one small omission before it became a larger problem.",
  "Clear ownership allowed the group to move forward without another meeting.",
  "The evidence did not answer everything, but it narrowed the next question.",
  "A reliable routine left more attention available for judgment and craft.",
] as const;

function proseItem(index: number): LaunchContentItemInput {
  const subject = choose(PROSE_SUBJECTS, index);
  const task = choose(PROSE_TASKS, Math.floor(index / PROSE_SUBJECTS.length));
  const observation = choose(PROSE_OBSERVATIONS, index + Math.floor(index / 20), 3);
  const ending = choose([
    "By the end, the next action felt specific and proportionate.",
    "The team kept the note brief enough to use and complete enough to trust.",
    "That balance supported accuracy, momentum, and a thoughtful finish.",
    "The process favored durable progress over a dramatic but fragile result.",
    "Later readers could understand the choice without reconstructing the entire day.",
  ], index, 9);
  return generatedItem(
    "prose-flow",
    index,
    `${subject} ${task}. ${observation} ${ending}`,
    ["mixed-case", "punctuation", "sentence-flow", "rhythm"],
    index % 5 === 0 ? "advanced" : index % 2 === 0 ? "foundation" : "standard",
  );
}

const DIALOGUE_NAMES = [
  "Mara", "Jonah", "Priya", "Eli", "Noor", "Caleb", "Iris", "Mateo", "June", "Theo",
  "Avery", "Nina", "Sam", "Rosa", "Owen", "Leah", "Kai", "Mina", "Dev", "Tessa",
] as const;

const DIALOGUE_REQUESTS = [
  "Can we confirm the owner before the handoff",
  "Did the revised note answer the original question",
  "Should we test the smaller change first",
  "Can the deadline move without creating another risk",
  "Did anyone record the reason for that decision",
  "Would a short example make this instruction clearer",
  "Can we separate the urgent issue from the important one",
  "Is the evidence strong enough to change the plan",
  "Should the summary include the unsuccessful attempt",
  "Can we finish the review before tomorrow’s meeting",
  "Did the customer receive a practical recovery step",
  "Would it help to name the remaining uncertainty",
  "Can the next person continue from these notes",
  "Should we remove the detail that no longer applies",
  "Did the final check preserve the writer’s meaning",
] as const;

function dialogueItem(index: number): LaunchContentItemInput {
  const first = choose(DIALOGUE_NAMES, index);
  const second = choose(DIALOGUE_NAMES, index + 7);
  const request = choose(DIALOGUE_REQUESTS, Math.floor(index / DIALOGUE_NAMES.length));
  const reply = choose([
    "Yes—I’ll add the evidence and send a clean version",
    "Not yet; one assumption still needs a careful check",
    "I think so, but let’s keep the limitation visible",
    "That’s reasonable—we can test it without delaying the rest",
    "I’ll revise it now, and you can challenge anything unclear",
  ], index, 3);
  const close = choose([
    "They agreed to review the change once more before calling it complete.",
    "The exchange was brief, but it turned a vague concern into a useful action.",
    "Neither person needed a long meeting after the decision was written down.",
    "The remaining disagreement became easier to discuss once the facts were separate.",
    "A precise follow-up replaced the earlier uncertainty with a workable plan.",
  ], index, 7);
  return generatedItem(
    "writing-dialogue",
    index,
    `“${request}?” ${first} asked. “${reply},” ${second} replied. ${close}`,
    ["quotes", "apostrophes", "em-dashes", "dialogue"],
    index % 4 === 0 ? "advanced" : "standard",
  );
}

function shiftItem(index: number): LaunchContentItemInput {
  const people = choose(DIALOGUE_NAMES, index);
  const place = choose(["North Hall", "Cedar Studio", "River Market", "Juniper Lab", "Harbor Library", "Maple Court", "Orchard Room", "Union Station", "Willow Park", "Brighton Center"], Math.floor(index / DIALOGUE_NAMES.length));
  const month = choose(["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], index, 5);
  const text = `${people} wrote, “Please meet me at ${place} on ${month} ${index % 27 + 1}.” The title—Final Review—stayed capitalized, but ordinary words did not.`;
  return generatedItem("shift-punctuation", index, text, ["capitalization", "punctuation", "shift-transitions", "quotes", "em-dashes"], index % 3 === 0 ? "advanced" : "standard");
}

const NUMBER_CONTEXTS = [
  "north warehouse", "community clinic", "transit office", "design studio", "public library",
  "repair workshop", "research archive", "training room", "garden center", "field station",
  "support desk", "shipping dock", "music hall", "market stall", "science lab",
  "project office", "service counter", "media room", "school kitchen", "visitor center",
] as const;

function numberItem(index: number): LaunchContentItemInput {
  const context = choose(NUMBER_CONTEXTS, index);
  const n = index + 11;
  const day = index % 27 + 1;
  const month = index % 12 + 1;
  const hour = index % 11 + 1;
  const minute = (index * 7) % 60;
  const amount = (18.5 + index * 2.37).toFixed(2);
  const rate = (3.5 + (index % 19) * 1.25).toFixed(2);
  const patterns: readonly [string, string[]][] = [
    [`The ${context} counted ${n * 14} units in ${n % 9 + 3} groups, with ${n + 8} units awaiting review.`, ["integers"]],
    [`On 2026-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}, the ${context} opened at ${hour}:${String(minute).padStart(2, "0")} and closed at ${hour + 8}:${String((minute + 15) % 60).padStart(2, "0")}.`, ["dates", "times"]],
    [`The ${context} completed ${rate}% of ${n * 9} requests before noon and another ${(Number(rate) + 12.5).toFixed(2)}% by 4:30 p.m.`, ["percent", "decimals", "times"]],
    [`Invoice MT-${4_000 + n} lists a subtotal of $${amount}, tax of $${(Number(amount) * 0.0725).toFixed(2)}, and a total due within 30 days.`, ["currency", "decimals", "integers"]],
    [`Measurements at the ${context} were ${(n / 3).toFixed(2)} cm, ${(n / 2).toFixed(1)} cm, and ${(n * 1.25).toFixed(2)} cm; record each decimal exactly.`, ["decimals", "punctuation-transitions"]],
    [`Call volume changed from ${n * 8} to ${n * 11}, an increase of ${(((n * 3) / (n * 8)) * 100).toFixed(1)}%; the comparison covers ${month}/${day}/2026 through ${month}/${Math.min(28, day + 4)}/2026.`, ["integers", "percent", "dates"]],
    [`The ${context} scheduled checkpoints for ${hour}:00, ${hour}:25, ${hour}:50, and ${hour + 1}:15; each window lasts ${n % 12 + 8} minutes.`, ["times", "integers"]],
    [`Budget line ${n % 7 + 1}.${n % 5 + 1} allows $${(n * 84.75).toFixed(2)}; ${rate}% remains reserved for unexpected work.`, ["currency", "percent", "decimals"]],
    [`Reference ${String(n).padStart(5, "0")}-${month}${day} records ${n * 3} items at ${(0.75 + index / 100).toFixed(2)} kg each.`, ["integers", "decimals", "dates"]],
    [`At the ${context}, ${n % 4 + 2}/${n % 7 + 8} of the work was complete by ${hour}:${String(minute).padStart(2, "0")}; the final count was ${n * 17}.`, ["integers", "times", "punctuation-transitions"]],
  ];
  const [text, focus] = choose(patterns, Math.floor(index / NUMBER_CONTEXTS.length));
  return generatedItem("numbers-data", index, text, [...focus, "numbers"], index % 4 === 0 ? "advanced" : "standard");
}

const SYMBOL_CONTEXTS = [
  "alpha", "bravo", "cedar", "delta", "ember", "field", "grove", "harbor", "indigo", "juniper",
  "keystone", "lantern", "meadow", "north", "orchard", "prairie", "quartz", "river", "summit", "timber",
] as const;

function symbolItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const n = index + 1;
  const texts = [
    `Set ${token}_${n} = (left + right) / 2; keep {braces}, [brackets], and (parentheses) balanced.`,
    `Search for @${token}-${n}, then tag #review & #ready; use ! only when the warning is urgent.`,
    `For ${token}, the range ${n}..${n + 8} maps input => output, while x >= ${n} && x <= ${n + 8} remains true.`,
    `Path /${token}/${n}?mode=review&sort=asc uses /, ?, =, &, and _ in one deliberate sequence.`,
    `Compare ${token}.${n}@example.test with ${token}+notes@example.test; both addresses require precise symbols.`,
    `In ${token}, write 5% + 7% = 12%, then compare $${n}.50, €${n}.75, and £${n}.25 without dropping a mark.`,
    "Use **" + token + "**, _detail_, ~~old~~, and `" + token + "_" + n + "` to practice paired symbol transitions.",
    `The ${token} expression !(a === b) || (count !== ${n}) combines !, ===, ||, !==, and parentheses.`,
    `Label <${token}-${n}> with "quotes", 'apostrophes', a colon: value, and a final semicolon;`,
    `Keys ^${token}$, ${token}*, and ${token}+ use ^, $, *, and +; type each pair without rushing.`,
  ];
  return generatedItem("symbols-operators", index, choose(texts, Math.floor(index / SYMBOL_CONTEXTS.length)), ["shifted-symbols", "punctuation-transitions", "paired-symbols"], index % 3 === 0 ? "advanced" : "standard");
}

function markdownItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const n = index + 1;
  const snippets = [
    `## ${token} review ${n}\n\n- [ ] Confirm the owner\n- [x] Link the evidence\n- [ ] Record the next step`,
    `Read [the ${token} guide](https://example.test/${token}/${n}) before changing **shared settings** or _local notes_.`,
    `> Decision ${n}: keep the ${token} workflow small.\n> Revisit it after the next measured trial.`,
    "1. Open the " + token + " file.\n2. Run `npm test -- --run " + n + "`.\n3. Record the result in **Review Notes**.",
    `### ${token} example\n\n\`\`\`text\nstatus: ready\nrevision: ${n}\n\`\`\``,
    `Use **bold** for the ${token} outcome, _italics_ for context, and ~~strikethrough~~ only for superseded step ${n}.`,
    `| ${token} field | Value |\n| --- | ---: |\n| Revision | ${n} |\n| Ready | yes |`,
    `- Parent ${token}\n  - Child note ${n}\n  - [Reference](https://example.test/reference/${n})`,
    "The command is `review --scope " + token + " --revision " + n + "`; do not place ordinary prose inside a code fence.",
    `# ${token} plan\n\n---\n\nKeep paragraph ${n} readable, link [source notes](https://example.test/${n}), and close with a decision.`,
  ];
  return generatedItem("markdown-docs", index, choose(snippets, Math.floor(index / SYMBOL_CONTEXTS.length)), ["headings", "links", "lists", "emphasis", "code-fences"], index % 4 === 0 ? "advanced" : "standard", "markdown");
}

function javascriptItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const id = `${token}${index + 1}`;
  const snippets: readonly [string, string][] = [
    [`const ${id} = records.filter((record) => record.status === "ready").map((record) => record.id);`, "javascript"],
    [`function clamp${id}(value, minimum, maximum) {\n  return Math.min(maximum, Math.max(minimum, value));\n}`, "javascript"],
    [`const total${id} = values.reduce((sum, value) => sum + value, 0);\nexport { total${id} };`, "javascript"],
    [`type ${id}Record<T> = { id: string; value: T; };\nconst first${id} = <T>(items: T[]): T | undefined => items[0];`, "typescript"],
    [`async function load${id}(request) {\n  const response = await request();\n  return response.ok ? response.value : null;\n}`, "javascript"],
    [`const ${id}ById = new Map(items.map((item) => [item.id, item]));\nconst selected${id} = ${id}ById.get(activeId) ?? null;`, "javascript"],
    [`interface ${id}Options { enabled: boolean; limit?: number; }\nconst normalize${id} = (options: ${id}Options) => ({ ...options, limit: options.limit ?? 10 });`, "typescript"],
    [`for (const item of queue) {\n  if (!item.enabled) continue;\n  results.push({ ...item, group: "${token}" });\n}`, "javascript"],
    [`const describe${id} = ({ title, count = 0 }) => \`${token}: \${title} (\${count})\`;`, "javascript"],
    [`class ${id}Store<T> {\n  #items: T[] = [];\n  add(item: T): void { this.#items.push(item); }\n  all(): readonly T[] { return this.#items; }\n}`, "typescript"],
  ];
  const [text, language] = choose(snippets, Math.floor(index / SYMBOL_CONTEXTS.length));
  return generatedItem("coding-symbols", index, text, ["braces", "arrows", "identifiers", "generics"], index % 3 === 0 ? "advanced" : "standard", language);
}

function pythonItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const id = `${token}_${index + 1}`;
  const className = `${token[0]!.toUpperCase()}${token.slice(1)}${index + 1}`;
  const snippets = [
    `def normalize_${id}(values):\n    cleaned = [value.strip() for value in values if value.strip()]\n    return sorted(set(cleaned))`,
    `def total_${id}(records):\n    return sum(record["value"] for record in records if record.get("ready"))`,
    `class ${className}:\n    def __init__(self, name):\n        self.name = name\n\n    def label(self):\n        return f"${token}: {self.name}"`,
    `async def load_${id}(client, path):\n    response = await client.get(path)\n    return response.json() if response.ok else None`,
    `pairs_${id} = {key: value for key, value in rows if value is not None}\nordered_${id} = sorted(pairs_${id}.items())`,
    `def chunks_${id}(items, size):\n    for offset in range(0, len(items), size):\n        yield items[offset:offset + size]`,
    `try:\n    result_${id} = parse(source)\nexcept ValueError as error:\n    result_${id} = {"error": str(error)}`,
    `from dataclasses import dataclass\n\n@dataclass(frozen=True)\nclass ${className}:\n    title: str\n    count: int = 0`,
    `def group_${id}(records):\n    groups = {}\n    for record in records:\n        groups.setdefault(record["status"], []).append(record)\n    return groups`,
    `message_${id} = "ready" if all(checks) else "review"\nprint(f"${token}: {message_${id}}")`,
  ];
  return generatedItem("python-code", index, choose(snippets, Math.floor(index / SYMBOL_CONTEXTS.length)), ["indentation", "colons", "comprehensions", "calls"], index % 3 === 0 ? "advanced" : "standard", "python");
}

function webItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const n = index + 1;
  const snippets: readonly [string, string][] = [
    [`<section class="${token}-panel" aria-labelledby="${token}-title-${n}">\n  <h2 id="${token}-title-${n}">${token} summary</h2>\n  <p>Revision ${n} is ready for review.</p>\n</section>`, "html"],
    [`.${token}-card-${n} {\n  display: grid;\n  gap: 1rem;\n  padding: 1.25rem;\n  border: 1px solid currentColor;\n}`, "css"],
    [`<label for="${token}-input-${n}">${token} name</label>\n<input id="${token}-input-${n}" name="${token}" autocomplete="off">`, "html"],
    [`.${token}-list-${n} > li + li {\n  margin-block-start: 0.75rem;\n}\n.${token}-list-${n}:focus-within {\n  outline: 3px solid #38bdf8;\n}`, "css"],
    [`<nav aria-label="${token} navigation">\n  <a href="/${token}/overview">Overview</a>\n  <a href="/${token}/history">History ${n}</a>\n</nav>`, "html"],
    [`@media (max-width: ${640 + (n % 4) * 80}px) {\n  .${token}-layout-${n} {\n    grid-template-columns: minmax(0, 1fr);\n  }\n}`, "css"],
    [`<button type="button" data-action="review-${token}" aria-describedby="${token}-help-${n}">Review</button>\n<small id="${token}-help-${n}">Checks revision ${n} without publishing.</small>`, "html"],
    [`.${token}-meter-${n} {\n  inline-size: min(100%, 42rem);\n  accent-color: #22c55e;\n  color: #f8fafc;\n}`, "css"],
    [`<details class="${token}-details-${n}">\n  <summary>Why this changed</summary>\n  <p>The evidence is preserved for the next review.</p>\n</details>`, "html"],
    [`.${token}-button-${n}:disabled {\n  cursor: not-allowed;\n  opacity: 0.65;\n}\n.${token}-button-${n}:focus-visible {\n  outline: 3px solid #facc15;\n}`, "css"],
  ];
  const [text, language] = choose(snippets, Math.floor(index / SYMBOL_CONTEXTS.length));
  return generatedItem("web-code", index, text, ["tags", "attributes", "selectors", "declarations"], index % 4 === 0 ? "advanced" : "standard", language);
}

function jsonItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index, 13);
  const text = JSON.stringify({
    id: `${token}-${index + 1}`,
    status: choose(["draft", "ready", "review", "complete"], index),
    revision: index + 1,
    tags: [token, "training", `group-${index % 8 + 1}`],
    settings: { enabled: index % 2 === 0, limit: index % 12 + 4 },
  }, null, 2);
  return generatedItem("structured-json", index, text, ["quotes", "delimiters", "indentation"], index % 3 === 0 ? "advanced" : "standard", "json");
}

function yamlItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index, 17);
  const n = index + 1;
  const text = `id: ${token}-${n}\nstatus: ${choose(["draft", "ready", "review", "complete"], index)}\nrevision: ${n}\ntags:\n  - ${token}\n  - training\nsettings:\n  enabled: ${index % 2 === 0 ? "true" : "false"}\n  limit: ${index % 12 + 4}`;
  return generatedItem("structured-yaml", index, text, ["delimiters", "indentation", "mapping"], index % 3 === 0 ? "advanced" : "standard", "yaml");
}

function sqlItem(index: number): LaunchContentItemInput {
  const token = choose(SYMBOL_CONTEXTS, index);
  const n = index + 1;
  const snippets = [
    `SELECT id, status, updated_at\nFROM ${token}_records\nWHERE status = 'ready'\nORDER BY updated_at DESC\nLIMIT ${n % 25 + 5};`,
    `SELECT category, COUNT(*) AS item_count\nFROM ${token}_items\nGROUP BY category\nHAVING COUNT(*) >= ${n % 8 + 2};`,
    `UPDATE ${token}_tasks\nSET status = 'complete', revision = revision + 1\nWHERE id = ${1_000 + n} AND status = 'ready';`,
    `INSERT INTO ${token}_notes (title, body, created_at)\nVALUES ('Review ${n}', 'Evidence recorded', CURRENT_TIMESTAMP);`,
    `SELECT u.name, COUNT(t.id) AS task_count\nFROM users AS u\nLEFT JOIN ${token}_tasks AS t ON t.owner_id = u.id\nGROUP BY u.id, u.name;`,
    `WITH recent_${token} AS (\n  SELECT * FROM events WHERE created_at >= CURRENT_DATE - INTERVAL '${n % 14 + 1} days'\n)\nSELECT COUNT(*) FROM recent_${token};`,
    `DELETE FROM ${token}_drafts\nWHERE expires_at < CURRENT_TIMESTAMP\n  AND published_at IS NULL\n  AND revision < ${n};`,
    `SELECT id, COALESCE(display_name, username) AS label\nFROM ${token}_members\nWHERE active = TRUE AND team_id = ${n % 12 + 1};`,
    `CREATE INDEX idx_${token}_${n}_status\nON ${token}_records (status, updated_at DESC);`,
    `SELECT DATE_TRUNC('day', created_at) AS day, AVG(duration_ms) AS average_ms\nFROM ${token}_runs\nGROUP BY DATE_TRUNC('day', created_at)\nORDER BY day;`,
  ];
  return generatedItem("sql-queries", index, choose(snippets, Math.floor(index / SYMBOL_CONTEXTS.length)), ["keywords", "punctuation", "identifiers"], index % 3 === 0 ? "advanced" : "standard", "sql");
}

const CHAT_CONTEXTS = [
  "release", "review", "support", "incident", "design", "research", "training", "schedule", "handoff", "inventory",
  "lobby", "match", "stream", "notes", "build", "deploy", "workshop", "report", "queue", "planning",
] as const;

function chatItem(index: number): LaunchContentItemInput {
  const context = choose(CHAT_CONTEXTS, index);
  const person = choose(DIALOGUE_NAMES, index, 11).toLocaleLowerCase("en-US");
  const n = index + 1;
  const messages = [
    `@${person} could you review the ${context} note before ${n % 11 + 1}:30? The open question is pinned.`,
    `/assign @${person} ${context}-${n} --due tomorrow --priority normal`,
    `Quick ${context} update: the check passed, one warning remains, and no action is blocked.`,
    `/join #${context}-${n % 20 + 1} then read the topic before posting a new thread.`,
    `gg! I can queue again after a ${n % 9 + 2}-minute break; @${person}, want the same role?`,
    `Can someone confirm whether ${context}-${n} is ready? A yes/no answer is enough for now.`,
    `/remind #${context} "review item ${n}" in ${n % 6 + 1} hours`,
    `Heads-up: ${context} latency reached ${n % 80 + 20} ms, but the error rate is still below 1%.`,
    `@${person} thanks—that fixed the ${context} issue. I’ll add the recovery step to the shared note.`,
    `/status ${context}-${n} --format short --include-owner`,
    `I’m stepping away for ${n % 20 + 5} minutes. Please tag @${person} only if the ${context} check fails.`,
    `The ${context} thread is getting long; I summarized the decision and next action at the top.`,
    `/mute #${context}-${n % 9 + 1} --for ${n % 4 + 1}h`,
    `Ready when you are. I have the ${context} notes open and can take the first response.`,
    `Could we move ${context}-${n} to tomorrow? The current window overlaps the final verification.`,
  ];
  return generatedItem("chat-commands", index, choose(messages, Math.floor(index / CHAT_CONTEXTS.length)), ["short-bursts", "slash-commands", "usernames"], index % 5 === 0 ? "advanced" : "foundation");
}

function researchItem(index: number): LaunchContentItemInput {
  const authors = ["Adams", "Bennett", "Chen", "Diaz", "Evans", "Farouk", "Gupta", "Harris", "Ito", "Jones", "Khan", "Lewis", "Morgan", "Nguyen", "Owens", "Patel", "Quinn", "Rivera", "Singh", "Turner"] as const;
  const author = choose(authors, index);
  const topic = choose(["guided practice", "error correction", "feedback timing", "reading ahead", "motor learning", "attention", "skill retention", "measurement", "practice spacing", "interface clarity"], index, 3);
  const year = 2017 + index % 10;
  const n = index + 1;
  const texts = [
    `${author} (${year}) reports a moderate association between ${topic} and durable accuracy, but the sample includes only ${n % 70 + 30} participants.`,
    `Field note ${n}, ${index % 27 + 1} June ${year}: ${author} recorded that participants described ${topic} as useful when the instructions remained brief and specific.`,
    `${author}, R. (${year}). “A practical study of ${topic}.” Journal of Applied Practice, ${n % 18 + 2}(${n % 4 + 1}), ${n % 90 + 10}–${n % 90 + 24}.`,
    `Research question ${n} from ${author}: Does ${topic} improve delayed performance when total practice time remains constant? Measure speed and accuracy separately.`,
    `The ${year} survey measured reported confidence rather than observed skill (${author}, ${year}). Treat the finding as suggestive, not conclusive.`,
    `${author} reviewed ${n % 6 + 3} small studies of ${topic}; results point in the same direction, but follow-up periods range from two days to six weeks.`,
    `Source comparison ${n}: ${author} (${year}) uses supervised sessions, whereas Nguyen (${Math.min(2026, year + 1)}) studies independent practice at home.`,
    `Method note ${n} by ${author}: observations about ${topic} were coded before the outcome was reviewed. Disagreements were resolved by a second researcher who had not seen the hypothesis.`,
    `Limitation ${n} noted by ${author}: the convenience sample may overrepresent people already interested in ${topic}. Replication should recruit through a broader channel.`,
    `Synthesis ${n} by ${author}: evidence for ${topic} is promising but uneven. Report the uncertainty, the measurement window, and the population before recommending a change.`,
  ];
  return generatedItem("research-citations", index, choose(texts, Math.floor(index / authors.length)), ["parentheses", "years", "names", "reference-patterns"], index % 3 === 0 ? "advanced" : "standard");
}

function commonVocabulary(): string[] {
  const sources: readonly string[][] = [
    gradeK as string[],
    grade1 as string[],
    grade2 as string[],
    grade3 as string[],
    grade4 as string[],
    grade5 as string[],
    grade6 as string[],
  ];
  const seen = new Set<string>();
  const words: string[] = [];
  for (const source of sources) {
    for (const candidate of source) {
      const word = candidate.normalize("NFKC").toLocaleLowerCase("en-US").trim();
      if (!/^[a-z]+(?:['-][a-z]+)?$/.test(word) || seen.has(word)) continue;
      seen.add(word);
      words.push(word);
      if (words.length === 1_000) return words;
    }
  }
  return words;
}

export const COMMON_WORDS = commonVocabulary();

export function buildCommonWordItems(): LaunchContentItemInput[] {
  return COMMON_WORDS.map((word, index) => {
    return {
      id: `common-words-${titleIndex(index)}`,
      title: `Common word ${titleIndex(index)}`,
      text: word,
      focusSkills: ["frequency-balanced", "common-words", "word-count"],
      difficulty: index < 400 ? "foundation" : index < 800 ? "standard" : "advanced",
      source: "MasterTyping graded English vocabulary, normalized and deduplicated",
      syntaxValidated: false,
    };
  });
}

type ItemFactory = (index: number) => LaunchContentItemInput;

const FACTORIES: Readonly<Record<string, ItemFactory>> = {
  "prose-flow": proseItem,
  "shift-punctuation": shiftItem,
  "writing-dialogue": dialogueItem,
  "numbers-data": numberItem,
  "symbols-operators": symbolItem,
  "markdown-docs": markdownItem,
  "coding-symbols": javascriptItem,
  "python-code": pythonItem,
  "web-code": webItem,
  "structured-json": jsonItem,
  "structured-yaml": yamlItem,
  "sql-queries": sqlItem,
  "chat-commands": chatItem,
  "research-citations": researchItem,
};

export function expandLaunchContentItems(
  packId: string,
  currentItems: readonly LaunchContentItemInput[],
): LaunchContentItemInput[] {
  const target = getAppendixEContentTarget(packId);
  if (!target || target.unit === "words" || packId === "adaptive-problem-keys") {
    return [...currentItems];
  }
  const factory = FACTORIES[packId];
  if (!factory || currentItems.length >= target.target) return [...currentItems];
  const generated = Array.from(
    { length: target.target - currentItems.length },
    (_, index) => factory(index),
  );
  return [...currentItems, ...generated];
}

export function contentTypeForTarget(
  target: AppendixEContentTarget,
): ProfessionalContentType {
  if (target.unit === "words") return "words";
  if (target.packId === "chat-commands") return "short-bursts";
  if (["markdown-docs", "coding-symbols", "python-code", "web-code"].includes(target.packId)) return "code";
  if (["structured-json", "structured-yaml", "sql-queries"].includes(target.packId)) return "structured-data";
  return "sentences";
}
